<template>
  <v-layout column>
    <tax-return-delivery-dialog
      v-if="currentTaxReturn"
      :key="currentTaxReturn.id"
      :opened.sync="reviewTaxReturnDialogOpened"
      :current-company-id="currentTaxReturn.company_id"
      :tax-return-id="currentTaxReturn.id"
      @refresh-table="refreshTable"
      @return-delivered="refreshTableUntilSent"
      @download-document="downloadDocument"
      @download-signed-pages-only="downloadSignedPagesOnly"
    />

    <audit-trail-dialog
      v-if="currentTaxReturn"
      :key="currentTaxReturn.id"
      :opened.sync="auditTrailDialogOpened"
      :taxReturn="currentTaxReturn"
    />

    <v-layout column>
      <div class="text-xs-left">Search</div>

      <v-text-field
        class="default-input subheading mb-2"
        label="Enter to search"
        v-model="taxReturnSearch"
        single-line
        solo
        hide-details
        append-icon="search"
      />
    </v-layout>

    <v-data-table
      :headers="headers"
      :items="taxReturnsMapped"
      class="text-xs-left"
      :search="taxReturnSearch"
      :custom-filter="customTaxReturnFilter"
      :pagination.sync="pagination"
      :rows-per-page-items="pagination.options"
      :loading="tableLoading"
      :class="
        $vuetify.breakpoint.mdAndUp
          ? 'overview-data-table'
          : 'mobile-overview-data-table'
      "
    >
      <template slot="items" slot-scope="props">
        <tr :key="props.index" slot="header">
          <td class="ph-no-capture">
            <a
              :href="`/admin-dashboard?companyId=${props.item.company_id}`"
              class="entity-name"
            >
              {{ props.item.company_name }}
            </a>
          </td>
          <td>
            {{ props.item.tax_year }}
          </td>
          <td>
            <v-layout
              align-center
              @click="viewTaxReturn(props.item)"
              style="cursor: pointer"
            >
              <u class="ph-no-capture">{{ getName(props.item) }}</u>
            </v-layout>
          </td>

          <td class="text-xs-left">
            <v-layout align-center>
              <i
                class="material-icons-outlined mr-2"
                :class="getTaxReturnStatus(props.item).class"
                >{{ getTaxReturnStatus(props.item).icon }}</i
              ><span class="font-weight-bold">{{
                getTaxReturnStatus(props.item).text
              }}</span>
            </v-layout>
          </td>

          <td class="text-xs-left">
            <v-tooltip bottom>
              <v-layout slot="activator">
                <sx-count-box
                  :count="props.item.completed_signature_count"
                  :total="props.item.total_signature_count"
                  :isDisabled="props.item.status == 'finished'"
                />
              </v-layout>

              <v-flex class="text-xs-left">
                <span v-if="props.item.total_signature_count === 0"
                  >No signatures requested.</span
                >
                <span
                  v-else-if="
                    props.item.total_signature_count ===
                      props.item.completed_signature_count
                  "
                  >All signatures completed</span
                >

                <span v-else-if="props.item.status === 'finished'"
                  >This item is marked as finished. Signatures are no longer
                  required.</span
                >

                <div v-else>
                  <b>
                    {{ props.item.remaining_signature_count }}
                    signature{{
                      props.item.remaining_signature_count === 1 ? 's' : ''
                    }}
                    still needed:
                  </b>

                  <div
                    v-if="
                      props.item.payment && props.item.payment.status !== 'paid'
                    "
                  >
                    • Tax Service Invoice - Due now
                  </div>

                  <div v-for="form in props.item.e_sign_forms">
                    <div v-for="signature in form.signatures">
                      <div v-if="!signature.signatureData">
                        <span>
                          •
                          <span>
                            <span
                              v-if="
                                form.form_id == 'other' &&
                                  !isEmpty(form.custom_form_name)
                              "
                              >{{ form.custom_form_name }}</span
                            >
                            <span v-else
                              >{{ getFormNameById(form.form_id) }} (Form
                              {{ form.form_id }})</span
                            >
                          </span>
                          -
                          {{ signature.title }}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </v-flex>
            </v-tooltip>
          </td>

          <td class="text-xs-left">
            <v-tooltip bottom>
              <v-layout slot="activator">
                <sx-count-box
                  :count="props.item.completed_payment_count"
                  :total="props.item.total_payment_count"
                />
              </v-layout>

              <v-flex class="text-xs-left">
                <span v-if="props.item.total_payment_count === 0"
                  >No tax payments requested.</span
                >
                <span
                  v-else-if="
                    props.item.total_payment_count ===
                      props.item.completed_payment_count
                  "
                  >All tax payments completed</span
                >
                <div v-else>
                  <b>
                    {{ props.item.remaining_payment_count }}
                    payments{{
                      props.item.remaining_payment_count === 1 ? 's' : ''
                    }}
                    still needed:
                  </b>

                  <div v-for="voucher in props.item.sorted_payment_vouchers">
                    <div v-if="!voucher.is_paid">
                      <span>
                        •
                        <span
                          v-if="
                            voucher.voucher_id === 'other' &&
                              !isEmpty(voucher.custom_payment_name)
                          "
                        >
                          {{ voucher.custom_payment_name }}
                        </span>
                        <span v-else>{{
                          getVoucherNameById(voucher.voucher_id)
                        }}</span>
                        <span> - Due date: {{ printDueDate(voucher) }}</span>
                      </span>
                    </div>
                  </div>
                </div>
              </v-flex>
            </v-tooltip>
          </td>

          <td class="text-xs-left">
            <span>{{ moment(props.item.updated_at).fromNow() }}</span>
          </td>

          <td class="text-xs-left">
            <span>{{
              props.item.last_reminder_sent_at
                ? moment(props.item.last_reminder_sent_at).fromNow()
                : 'N/A'
            }}</span>
          </td>

          <td class="text-xs-left">
            <v-switch
              :disabled="props.item.all_signed && props.item.all_paid"
              :input-value="
                props.item.all_signed && props.item.all_paid
                  ? false
                  : !props.item.reminder_disabled
              "
              @change="toggleReminder(props.item, $event)"
              hide-details
            ></v-switch>
          </td>

          <td>
            <v-layout align-center justify-center>
              <v-menu
                transition="slide-y-transition"
                bottom
                left
                nudge-bottom="30"
                class="my-1"
              >
                <v-icon slot="activator" color="#0a1a25"> more_vert </v-icon>

                <v-list>
                  <v-list-tile @click="remove(props.item)">
                    <v-list-tile-title> Remove </v-list-tile-title>
                  </v-list-tile>

                  <v-list-tile
                    v-if="
                      props.item.status === 'sent' ||
                        props.item.status === 'finished'
                    "
                    @click="sendReminders(props.item)"
                  >
                    <v-list-tile-title> Send reminder </v-list-tile-title>
                  </v-list-tile>

                  <v-list-tile
                    v-if="
                      props.item.status === 'sent' ||
                        props.item.status === 'finished'
                    "
                    @click="viewAuditTrail(props.item)"
                  >
                    <v-list-tile-title>
                      View audit trail
                    </v-list-tile-title>
                  </v-list-tile>

                  <v-list-tile
                    v-if="props.item.status === 'sent'"
                    @click="markAsFinished(props.item)"
                  >
                    <v-list-tile-title>
                      Mark as finished
                    </v-list-tile-title>
                  </v-list-tile>
                </v-list>
              </v-menu>
            </v-layout>
          </td>
        </tr>
      </template>
    </v-data-table>
  </v-layout>
</template>

<script>
import UploadTaxReturnDialog from '@/components/returns/UploadTaxReturnDialog';
import TaxReturnDeliveryDialog from '@/components/returns/TaxReturnDeliveryDialog';
import API from '@/api';
import moment from 'moment';
import isEmpty from 'lodash/isEmpty';
import { mapGetters } from 'vuex';
import { yearOptions } from '@/constants/variables';
import TaxReturnsMixin from '@/mixins/TaxReturnsMixin';
import axios from 'axios';
import SxCountBox from '@/components/sx/SxCountBox';
import AuditTrailDialog from '@/components/returns/AuditTrailDialog';

export default {
  components: {
    TaxReturnDeliveryDialog,
    UploadTaxReturnDialog,
    SxCountBox,
    AuditTrailDialog,
  },

  mixins: [TaxReturnsMixin],

  props: {
    initialYear: {
      optional: true,
    },

    taxReturns: {
      type: Array,
    },

    tableLoading: {
      type: Boolean,
    },
  },

  data() {
    return {
      currentYear: 'All',

      taxReturnSearch: '',

      headers: [
        {
          text: 'Entity',
          align: 'left',
          width: '15%',
          value: 'company_name',
          sortable: true,
        },
        {
          text: 'Tax Year',
          align: 'left',
          value: 'tax_year',
          width: '5%',
          sortable: true,
        },
        {
          text: 'Tax Return',
          align: 'left',
          value: 'name',
          width: '15%',
          sortable: true,
        },
        {
          text: 'Status',
          align: 'left',
          value: 'tax_return_status.value',
          width: '15%',
          sortable: true,
        },
        {
          text: 'Signatures',
          align: 'left',
          sortable: true,
          value: 'completed_signature_count',
          width: '10%',
        },
        {
          text: 'Payments',
          align: 'left',
          sortable: true,
          value: 'completed_payment_count',
          width: '10%',
        },
        {
          text: 'Last Activity',
          align: 'left',
          width: '10%',
          value: 'updated_at',
          sortable: true,
        },
        {
          text: 'Last Reminder Sent',
          align: 'left',
          width: '10%',
          value: 'last_reminder_sent_at',
        },
        {
          text: 'Reminder',
          align: 'left',
          sortable: true,
          width: '5%',
        },
        {
          text: 'Actions',
          align: 'center',
          width: '5%',
          sortable: false,
        },
      ],

      pagination: {
        descending: true,
        rowsPerPage: 25,
      },

      reviewTaxReturnDialogOpened: false,
      auditTrailDialogOpened: false,

      currentTaxReturnId: null,
    };
  },

  computed: {
    ...mapGetters({
      currentFirm: 'currentFirm',
    }),

    yearOptionsWithAll() {
      return ['All', ...yearOptions.map(c => c.toString())];
    },

    currentTaxReturn() {
      return this.taxReturns.find(
        taxReturn => taxReturn.id === this.currentTaxReturnId,
      );
    },
  },

  methods: {
    moment,
    isEmpty,

    viewAuditTrail(item) {
      this.currentTaxReturnId = item.id;
      this.auditTrailDialogOpened = true;
    },

    refreshTable() {
      this.$emit('refresh-table');
    },

    refreshTableUntilSent(returnId) {
      this.$emit('refresh-table-until-sent', returnId);
    },

    customTaxReturnFilter(items, search) {
      const searchedItem = search.toLowerCase();

      return items.filter(row => {
        let rowExists = true;

        if (searchedItem.length) {
          rowExists =
            row.tax_return_name.toLowerCase().includes(searchedItem) ||
            row.company_name.toLowerCase().includes(searchedItem);
        }

        return rowExists;
      });
    },
  },

  async created() {
    await this.getVoucherDefinitions();
    await this.getFormDefinitions();
  },

  watch: {
    async initialYear() {
      this.currentYear = this.initialYear;
    },

    async currentYear() {
      this.$emit('refresh-table');
    },

    reviewTaxReturnDialogOpened() {
      if (!this.reviewTaxReturnDialogOpened) {
        this.currentTaxReturnId = null;
      }
    },
  },
};
</script>

<style lang="postcss" scoped>
.entity-name {
  text-decoration: none;
  color: #333;
}
</style>
